import { Button, Grid } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useState } from 'react';
import { DocumentReference } from 'src/careflow/ph-core/fhir-client/fhir-base/mappings/DocumentReference';
import { CarePlanWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/CarePlan';
import { DocumentReferenceWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/DocumentReference';
import { useBinarys, useCarePlan, useDocumentReferences } from 'src/careflow/ph-core/fhir-client/fhir-react';
import usePrintableCarePlan from 'src/careflow/care-coordination/case/printable-care-plan/usePrintableCarePlan';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import useTenantConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';
import useWebMessageListener from 'src/careflow/care-coordination/case/printable-care-plan/useWebMessageListener';
import Viewer from 'src/careflow/care-coordination/patient/assessment/viewer/Viewer';
import { getIdFromReference } from 'src/utils/fhir';
import { useStandardLoadingBackdrop } from 'src/careflow/ph-core/ui/base/StandardLoadingBackdropProvider';
import { useStandardDialog } from 'src/careflow/ph-core/ui/base/StandardDialogProvider';

interface PrintableCarePlanPropsI {
  carePlanId: string | undefined;
  renderButton?: (onClick: () => void) => JSX.Element;
}

export function PrintableCarePlan({ carePlanId, renderButton }: PrintableCarePlanPropsI) {
  const { configurations } = useTenantConfigData();
  const printableCarePlan = usePrintableCarePlan(configurations);
  const loadingBackdrop = useStandardLoadingBackdrop();
  const [openPrintableCarePlan, setOpenPrintableCarePlan] = useState<boolean>(false);
  const standardDialog = useStandardDialog();
  const webMessageListener = useWebMessageListener({ handleMessage: handleWebMessage });
  const [, { create: createBinary }] = useBinarys({ autofetch: false });
  const [documentReferencesRecords, { create: createDocumentReference }] = useDocumentReferences({
    map: DocumentReferenceWrapper,
  });

  const [carePlan] = useCarePlan(carePlanId, { map: CarePlanWrapper });
  const { enqueueSnackbar } = useSnackbar();
  const _patientId = useMemo(() => {
    if (!carePlan?.subject?.reference) {
      return;
    }
    return getIdFromReference(carePlan.subject.reference);
  }, [carePlan]);
  const { i18n } = useLocales();

  function handleWebMessage(e: MessageEvent<any>) {
    const allowedOriginsToHandleIframeMessages = [''];

    if (!allowedOriginsToHandleIframeMessages.includes(e.origin)) {
      return;
    }

    if (!e.data || e.data.topic !== 'iframe-printable-care-plan') {
      return;
    }
  }

  useEffect(() => {
    webMessageListener.enable();
  }, []);

  function getTimestampPrefix() {
    const now = new Date();

    // Pad single-digit numbers with a leading zero
    const pad = (num) => String(num).padStart(2, '0');

    const year = now.getFullYear();
    const month = pad(now.getMonth() + 1); // getMonth() is 0-indexed
    const day = pad(now.getDate());
    const hours = pad(now.getHours());
    const minutes = pad(now.getMinutes());
    const seconds = pad(now.getSeconds());

    return `${year}-${month}-${day}_${hours}-${minutes}-${seconds}`;
  }

  function downloadHandler(data: Blob) {
    console.debug('DEBUG PrintableCarePlan starting downloading printable care plan ...');

    if (!data) {
      enqueueSnackbar('failed to download', { variant: 'error' });
      return;
    }

    let fileName = getTimestampPrefix();

    fileName += data?.type === 'application/pdf' ? '_printable.pdf' : '_printable.html';

    const url = URL.createObjectURL(data);

    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.style.display = 'none';

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    URL.revokeObjectURL(url);
  }

  function printHandler(data: Blob) {
    let message: any = {
      topic: 'iframe-printable-care-plan',
      task: 'print',
    };

    //@ts-ignore
    let win = window.frames.printable_care_plan_iframe;

    if (data?.type === 'application/pdf') {
      message = {
        topic: 'document-viewer',
        action: 'print-pdf',
      };

      //@ts-ignore
      win = window.frames.documentPreview;
    }

    if (!win) {
      enqueueSnackbar('failed to print care plan', { variant: 'error' });
      return;
    }

    webMessageListener.send(message, win);
  }

  const handleCreateBynary = async (data: any) => {
    const result = await createBinary(data);
    return result;
  };

  const convertBlobToBase64 = (file: Blob) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = reject;
      reader.onload = () => {
        resolve(reader.result?.toString().split(',')[1]);
      };
      reader.readAsDataURL(file);
    });

  const createBinaryCustom = async (fileName: string, file: Blob): Promise<any> => {
    const resourceType = 'Binary';
    const data = await convertBlobToBase64(file);
    console.debug('DEBUG PrintableCarePlan createBinaryCustom after convertBlobToBase64');
    const name = fileName;
    const obj = { resourceType, contentType: file.type, data };
    const binary = await handleCreateBynary(obj);
    const objNew = { ...binary[0], name: name };
    return objNew;
  };

  const mapFormDataToDocumentReference = (binary: any, patId: string) => {
    const documentReference: DocumentReference = {
      resourceType: 'DocumentReference',
      status: 'current',
      date: new Date().toISOString(),
      subject: {
        reference: `Patient/${patId}`,
        type: 'Patient',
      },
      content: [
        {
          attachment: {
            contentType: `${binary?.contentType}`,
            creation: new Date().toISOString(),
            title: binary.name,
            url: `${configurations?.fhir?.baseUrl}/Binary/${binary?.id}`,
          },
        },
      ],
    };

    return documentReference;
  };

  async function saveAttachment(data: Blob | undefined, patId: string) {
    try {
      const fileName = getTimestampPrefix() + '_printable_care_plan';

      if (!data) {
        enqueueSnackbar('failed to save attachment', { variant: 'error' });
        return;
      }

      const resultBinary = await createBinaryCustom(fileName, data);
      console.debug('DEBUG PrintableCarePlan after resultBinary');
      const mapBinaryToDocumentReference = mapFormDataToDocumentReference(resultBinary, patId);
      const saveDocumentReference = await createDocumentReference(mapBinaryToDocumentReference);

      enqueueSnackbar('Attachment was created');
    } catch (error) {
      enqueueSnackbar('Attachment was not created', { variant: 'error' });
    }
  }

  function getShowElement(iframeSrc, patId, srcContentType, stringSrc) {
    return (
      <Grid container direction="column">
        <Grid item xs={12}>
          {srcContentType === 'application/pdf' ? (
            <Viewer file={iframeSrc} typeResource="blob" />
          ) : (
            <iframe
              name="printable_care_plan_iframe"
              title="Printable CarePlan"
              style={{ minHeight: '70vh', width: '100%', height: '100%' }}
              srcDoc={stringSrc}
              sandbox="allow-scripts allow-modals"
            ></iframe>
          )}
        </Grid>

        <Grid
          container
          padding={2}
          spacing={1}
          direction="row"
          justifyContent="flex-end"
          alignItems="flex-end"
        >
          <Grid item>
            <Button
              type="button"
              onClick={() => {
                setOpenPrintableCarePlan(false);
                standardDialog.close();
              }}
            >
              Close
            </Button>
          </Grid>

          <Grid item>
            <Button type="button" variant="contained" onClick={() => downloadHandler(iframeSrc)}>
              Download
            </Button>
          </Grid>

          <Grid item>
            <Button type="button" variant="contained" onClick={() => printHandler(iframeSrc)}>
              Print
            </Button>
          </Grid>

          <Grid item>
            <Button
              type="button"
              variant="contained"
              onClick={() => saveAttachment(iframeSrc, patId)}
            >
              Save as Attachment
            </Button>
          </Grid>
        </Grid>
      </Grid>
    );
  }

  async function loadPrintable() {
    if (!carePlanId) {
      return;
    }

    const result = await printableCarePlan.getJasperPrintableCareplan(carePlanId);

    console.debug('DEBUG PrintableCarePlan result.type: ', result?.type);

    if (!result) {
      return;
    }

    loadingBackdrop.close();

    let blobToString = await result.text();
    blobToString = blobToString.replace('<!DOCTYPE html>', '');

    standardDialog.open(getShowElement(result, _patientId, result?.type, blobToString));
  }

  useEffect(() => {
    if (!openPrintableCarePlan) {
      return;
    }

    loadingBackdrop.open();

    if (!_patientId) {
      return;
    }

    loadPrintable();
  }, [openPrintableCarePlan, _patientId]);

  return renderButton ? (
    renderButton(() => {
      setOpenPrintableCarePlan(true);
    })
  ) : (
    <Button
      type="button"
      variant="contained"
      onClick={() => {
        setOpenPrintableCarePlan(true);
      }}
    >
      {i18n('case.details.generatePrintableCarePlan', 'crs')}
    </Button>
  );
}
